// Adding the units a non-conformance covers, by service tag (issue #748).
//
// Typing a tag looks it up in production data and fills in what it already
// knows — product number, model — instead of asking someone to copy it across
// from another tab. The unit keeps a link to its device page, so the production
// record behind a complaint is one click away.
//
// An unknown tag is NOT refused. Sometimes the reason for raising the NCR is
// that a unit was never tracked, and blocking that would block the case that
// most needs raising. It is accepted on confirmation and marked unverified,
// because an unverified unit that looks verified is worse than no traceability
// at all — you would trust it.

import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';

const API_BASE = process.env.REACT_APP_API_URL;

const pill = {
  fontSize: '0.62rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  borderRadius: 999,
  padding: '0.05rem 0.4rem',
  flexShrink: 0,
};

const verifiedPill = {
  ...pill,
  color: colors.primary,
  background: 'rgba(70,187,137,0.14)',
  border: '1px solid rgba(70,187,137,0.35)',
};

const unverifiedPill = {
  ...pill,
  color: '#fbbf24',
  background: 'rgba(251,191,36,0.12)',
  border: '1px solid rgba(251,191,36,0.35)',
};

export default function ComplaintUnitPicker({ units, onChange, onAutofill }) {
  const { getToken } = useAuth();
  const [tag, setTag] = useState('');
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [unknown, setUnknown] = useState(null);      // a tag production data does not know
  const [existing, setExisting] = useState([]);      // NCRs already on the unit just added

  const alreadyAdded = candidate =>
    units.some(u =>
      (u.serviceTagNumber && u.serviceTagNumber.toLowerCase() === candidate.toLowerCase()) ||
      (u.serialNumber && u.serialNumber.toLowerCase() === candidate.toLowerCase()));

  const add = (unit) => {
    onChange([...units, unit]);
    setTag('');
    setUnknown(null);
    // The first verified unit fills the form's own product number, which is
    // what someone would otherwise copy across by hand.
    if (unit.productNumber && onAutofill) onAutofill(unit);
  };

  const lookup = async () => {
    const candidate = tag.trim();
    if (!candidate) return;
    if (alreadyAdded(candidate)) {
      setError('That unit is already on this complaint.');
      return;
    }
    setChecking(true);
    setError('');
    setUnknown(null);
    setExisting([]);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/complaints/device-lookup/${encodeURIComponent(candidate)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setError('Could not check that tag. Please try again.');
        return;
      }
      if (!data.found) {
        setUnknown(candidate);
        return;
      }
      add({ ...data.unit, confirmedUnverified: false });
      setExisting(data.existingComplaints || []);
    } catch (err) {
      setError('Could not check that tag. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const confirmUnknown = () => {
    add({
      serviceTagNumber: unknown,
      serialNumber: unknown,
      productNumber: '',
      model: '',
      deviceId: '',
      verified: false,
      confirmedUnverified: true,
    });
  };

  // Removing an autofilled unit has to tell the form, or the complaint keeps
  // the serial of a unit that is no longer on it -- a traceability record
  // pointing at the wrong thing, which is worse than an empty one.
  const remove = (idx) => {
    const next = units.filter((_, i) => i !== idx);
    onChange(next, units[idx], next[0] || null);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'flex-start' }}>
        <input
          value={tag}
          onChange={e => { setTag(e.target.value); setError(''); setUnknown(null); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); lookup(); } }}
          placeholder="Service tag or serial…"
          className="ni-input"
          style={{ flex: 1, minWidth: 0, fontSize: '0.85rem' }}
          aria-label="Service tag or serial number"
        />
        <button
          type="button"
          onClick={lookup}
          disabled={checking || !tag.trim()}
          className="ni-button ni-button--ghost"
          style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem', flexShrink: 0 }}
        >
          {checking ? 'Checking…' : 'Add unit'}
        </button>
      </div>

      {error && (
        <p style={{ margin: '0.35rem 0 0', fontSize: '0.78rem', color: colors.danger }}>{error}</p>
      )}

      {unknown && (
        <div style={{
          marginTop: '0.45rem', padding: '0.5rem 0.6rem', borderRadius: 8,
          background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)',
        }}>
          <p style={{ margin: 0, fontSize: '0.8rem', color: colors.textBody, lineHeight: 1.5 }}>
            <strong>{unknown}</strong> is not in the production data. Are you sure this is the
            service tag? It will be recorded as unverified — which is often the point, if the
            unit was never tracked.
          </p>
          <div style={{ display: 'flex', gap: '0.4rem', marginTop: '0.45rem' }}>
            <button
              type="button"
              onClick={confirmUnknown}
              className="ni-button ni-button--ghost"
              style={{ fontSize: '0.76rem', padding: '0.22rem 0.6rem' }}
            >
              I confirm — add it anyway
            </button>
            <button
              type="button"
              onClick={() => setUnknown(null)}
              className="ni-button ni-button--ghost"
              style={{ fontSize: '0.76rem', padding: '0.22rem 0.6rem', opacity: 0.7 }}
            >
              Let me re-check it
            </button>
          </div>
        </div>
      )}

      {existing.length > 0 && (
        <p style={{
          margin: '0.45rem 0 0', fontSize: '0.78rem', lineHeight: 1.5,
          color: '#fbbf24', background: 'rgba(251,191,36,0.08)',
          border: '1px solid rgba(251,191,36,0.28)', borderRadius: 8, padding: '0.4rem 0.55rem',
        }}>
          This unit already has {existing.length === 1 ? 'a non-conformance' : 'non-conformances'}:{' '}
          {existing.map(c => `${c.complaintId} (${c.status})`).join(', ')}. Check it is not the
          same defect before raising another.
        </p>
      )}

      {units.length > 0 && (
        <div style={{ marginTop: '0.5rem' }}>
          {units.map((u, i) => (
            <div
              key={u.serviceTagNumber || u.serialNumber || i}
              style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                padding: '0.4rem 0.55rem', marginBottom: '0.3rem',
                borderRadius: 8, border: `1px solid ${colors.panelBorder}`,
                background: 'rgba(255,255,255,0.04)',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.84rem', color: colors.textStrong, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {u.serviceTagNumber || u.serialNumber}
                </div>
                <div style={{ fontSize: '0.74rem', color: colors.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {[u.productNumber, u.model].filter(Boolean).join(' · ') || 'No production record'}
                </div>
              </div>

              <span style={u.verified ? verifiedPill : unverifiedPill}>
                {u.verified ? 'Verified' : 'Unverified'}
              </span>

              {u.deviceId && (
                <a
                  href={`/device/${u.deviceId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontSize: '0.74rem', color: colors.primary, textDecoration: 'none', flexShrink: 0 }}
                >
                  Device ↗
                </a>
              )}

              <button
                type="button"
                onClick={() => remove(i)}
                style={{ background: 'none', border: 'none', color: colors.textMuted, cursor: 'pointer', fontSize: '0.8rem', flexShrink: 0 }}
                aria-label={`Remove ${u.serviceTagNumber || u.serialNumber}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
